import React from 'react';
import styled from 'styled-components';
import { Container, Text } from '../styles/components';
import SectionNumber from './SectionNumber';

const SectionWrapper = styled.section`
  min-height: calc(100svh - 100px);
  padding: 100px 0 ${props => props.theme.spacing.xxl} 0;
  position: relative;
  background: ${props => props.theme.colors.bone};
  display: flex;
  align-items: center;
`;

const ContentContainer = styled(Container)`
  max-width: ${props => props.theme.spacing.maxWidth};
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const HeaderWrapper = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xxl};
`;

const SectionHeading = styled.h2`
  font-family: ${props => props.theme.typography.heading.fontFamily};
  font-weight: ${props => props.theme.typography.heading.fontWeight};
  font-size: 2.5rem;
  line-height: 1.1;
  color: ${props => props.theme.colors.charcoal};
  margin: 0 0 ${props => props.theme.spacing.md} 0;
`;

const Rule = styled.hr`
  width: 180px;
  height: 0.75px;
  background-color: ${props => props.theme.colors.charcoal};
  border: none;
  margin: 0 auto;
  opacity: 0.5;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.xxl};
  max-width: 1000px;
  margin: 0 auto;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.xl};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    gap: ${props => props.theme.spacing.lg};
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const ColumnHeader = styled.h3`
  font-family: ${props => props.theme.typography.heading.fontFamily};
  font-size: ${props => props.theme.typography.sizes.h4};
  color: ${props => props.theme.colors.charcoal};
  margin: 0 0 ${props => props.theme.spacing.md} 0;
  padding-bottom: ${props => props.theme.spacing.sm};
  border-bottom: 2px dotted ${props => props.theme.colors.blueMidGrey};
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const ListItem = styled.li`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  align-items: flex-start;
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
  color: ${props => props.$type === 'success' ? props.theme.colors.sage : props.theme.colors.terracotta};
  font-size: 1.5rem;
  line-height: 1;
`;

const ItemText = styled(Text)`
  color: ${props => props.theme.colors.charcoal};
  line-height: 1.5;
  margin: 0;
`;

const CheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CrossIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IsThisForYouSection = () => {
  return (
    <SectionWrapper id="is-this-for-you">
      <ContentContainer>
        <HeaderWrapper>
          <SectionHeading>Is this for you?</SectionHeading>
          <Rule />
        </HeaderWrapper>

        <Grid>
          <Column>
            <ColumnHeader>This is for you if:</ColumnHeader>
            <List>
              <ListItem>
                <IconWrapper $type="success"><CheckIcon /></IconWrapper>
                <ItemText>You're raising Seed+ in hard tech, deep tech, or frontier tech (robotics, materials science, energy, defense, space, hardware, climate tech, biotech)</ItemText>
              </ListItem>
              <ListItem>
                <IconWrapper $type="success"><CheckIcon /></IconWrapper>
                <ItemText>You've pitched 10+ investors and can't get momentum OR you're about to start fundraising and want to do it right</ItemText>
              </ListItem>
              <ListItem>
                <IconWrapper $type="success"><CheckIcon /></IconWrapper>
                <ItemText>Your technology works but the investment story feels unclear, or you keep getting "interesting but not for us"</ItemText>
              </ListItem>
              <ListItem>
                <IconWrapper $type="success"><CheckIcon /></IconWrapper>
                <ItemText>You're willing to challenge your own assumptions about positioning and market strategy</ItemText>
              </ListItem>
            </List>
          </Column>

          <Column>
            <ColumnHeader>This is NOT for you if:</ColumnHeader>
            <List>
              <ListItem>
                <IconWrapper $type="error"><CrossIcon /></IconWrapper>
                <ItemText>You just need slides made pretty (hire a designer - I can recommend some)</ItemText>
              </ListItem>
              <ListItem>
                <IconWrapper $type="error"><CrossIcon /></IconWrapper>
                <ItemText>You want someone to "write the deck for you" without strategic input. I work collaboratively.</ItemText>
              </ListItem>
              <ListItem>
                <IconWrapper $type="error"><CrossIcon /></IconWrapper>
                <ItemText>You're raising less than £500k (my pricing won't make sense at that stage)</ItemText>
              </ListItem>
            </List>
          </Column>
        </Grid>
      </ContentContainer>
      <SectionNumber number={5} light={true} />
    </SectionWrapper>
  );
};

export default IsThisForYouSection;
